import { cn } from '@/lib/utils';
import { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.tsx';
import { Edit, ListPlus, Plus, Trash } from 'lucide-react';
import TourOptionAddModal from '@/features/dashboard/tourOptions/components/TourOptionAddModal.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { TourOptionDto } from '@/features/dashboard/tourOptions/types/TourOptionDto.ts';
import Chip from '@/components/ui/Chip.tsx';
import useGetTourOptions from '@/features/dashboard/tourOptions/hooks/useGetTourOptions.ts';
import useGetTourOptionUsage from '@/features/dashboard/tourOptions/hooks/useGetTourOptionUsage.ts';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import useRemoveTourOption from '@/features/dashboard/tourOptions/hooks/useRemoveTourOption.ts';
import useReorderTourOptions from '@/features/dashboard/tourOptions/hooks/useReorderTourOptions.ts';
import { tourOptionsConfigRoute } from '@/features/dashboard/tourOptions/routes/tourOptionsConfigRoute.ts';
import TourOptionEditModal from '@/features/dashboard/tourOptions/components/TourOptionEditModal.tsx';
import {
  SortableList,
  SortableListItem,
  SortableListDragHandle,
} from '@/components/ui/SortableList.tsx';

const TourOptionsConfigCard = () => {
  const { tourId } = tourOptionsConfigRoute.useParams();
  const { data: tourOptions } = useGetTourOptions(tourId);
  const { confirmDeleteWithCallback } = confirmDialogUseHook();
  const { mutate: removeTourOption } = useRemoveTourOption(tourId);
  const { mutate: reorderTourOptions } = useReorderTourOptions(tourId);
  const [editTourOptionDto, setEditTourOptionDto] = useState<
    TourOptionDto | undefined
  >(undefined);

  const [isTourOptionAddModalOpen, setIsTourOptionAddModalOpen] =
    useState(false);

  const handleRemoveTourOption = (optionId: string) => {
    confirmDeleteWithCallback({
      title: 'Usuń opcję dodatkową',
      description: 'Czy na pewno chcesz usunąć tę opcję dodatkową?',
      confirmText: 'Usuń opcję',
      loadingText: 'Usuwanie...',
      onConfirm: () => {
        removeTourOption(optionId);
      },
    });
  };

  const openTourOptionAddModal = () => {
    setEditTourOptionDto(undefined);
    setIsTourOptionAddModalOpen(true);
  };

  const openTourOptionEditModal = (tourOption: TourOptionDto) => {
    setEditTourOptionDto(tourOption);
  };

  return (
    <Card>
      <CardHeader className="border-b p-4">
        <CardTitle>Opcje dodatkowe</CardTitle>
        <CardDescription>
          Dodaj dodatkowe atrakcje i ich koszty. Możesz uzależnić je od wieku i
          dodać różne warianty.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {isTourOptionAddModalOpen && (
          <TourOptionAddModal
            onClose={() => setIsTourOptionAddModalOpen(false)}
          />
        )}

        {editTourOptionDto !== undefined && (
          <TourOptionEditModal
            onClose={() => setEditTourOptionDto(undefined)}
            tourOptionDto={editTourOptionDto}
          />
        )}

        {!tourOptions || tourOptions.length === 0 ? (
          <NoActivitiesPlaceholder openModal={openTourOptionAddModal} />
        ) : (
          <>
            <SortableList<TourOptionDto>
              items={tourOptions}
              getId={(option) => option.id}
              getOrderIndex={(option) => option.orderIndex}
              onReorder={reorderTourOptions}
              className="flex flex-col gap-1 bg-neutral-50 p-4 py-2"
            >
              {(tourOption) => (
                <SortableListItem id={tourOption.id} asChild>
                  <TourOptionRow
                    tourOption={tourOption}
                    onEdit={() => openTourOptionEditModal(tourOption)}
                    onRemove={() => handleRemoveTourOption(tourOption.id)}
                  />
                </SortableListItem>
              )}
            </SortableList>

            <CardContent className="py-2">
              <ActionButton
                tone="outline"
                size="sm"
                onClick={openTourOptionAddModal}
              >
                <Plus />
                Dodaj opcję dodatkową
              </ActionButton>
            </CardContent>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const TourOptionRow = ({
  tourOption,
  onEdit,
  onRemove,
  className,
  ...props
}: {
  tourOption: TourOptionDto;
  onEdit: () => void;
  onRemove: () => void;
} & React.HTMLAttributes<HTMLDivElement>) => {
  const { data: usage } = useGetTourOptionUsage(tourOption.id);
  const isUsed = usage?.used === true;

  return (
    <Card
      // `SortableListItem asChild` przekazuje `className: undefined`, które bez
      // scalenia wyzerowałoby klasy wiersza.
      className={cn(
        'flex flex-row items-center justify-between bg-basic-white p-1.5',
        className,
      )}
      {...props}
    >
      <div className="flex flex-row items-center gap-2">
        <div className="flex flex-row items-center gap-1">
          <SortableListDragHandle />
          <div className="text-sm font-semibold text-base-text">
            {tourOption.name}
          </div>
        </div>
        <div className="flex flex-row items-center gap-1">
          {tourOption.required && (
            <Chip label="Obowiązkowe" tone="neutral" fill="soft" />
          )}
          {tourOption.variants.length > 1 && (
            <Chip
              label={`${tourOption.variants.length} warianty`}
              tone="accent"
              fill="soft"
            />
          )}
          {tourOption.archived && (
            <Chip label="Wycofana z oferty" tone="neutral" fill="soft" />
          )}
        </div>
      </div>
      <div className="flex flex-row items-center gap-2">
        <div className="flex items-center gap-1">
          <button
            onClick={onEdit}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-neutral-600 hover:bg-neutral-100 hover:text-neutral-700"
            title="Edytuj opcję"
          >
            <Edit className="size-4" />
          </button>
          <button
            onClick={onRemove}
            disabled={isUsed}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-neutral-600 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-neutral-600"
            title={
              isUsed
                ? 'Nie można usunąć — opcja jest używana w rezerwacjach. Zamiast tego oznacz ją jako wycofaną z oferty.'
                : 'Usuń opcję'
            }
          >
            <Trash className="size-4" />
          </button>
        </div>
      </div>
    </Card>
  );
};

const NoActivitiesPlaceholder = ({ openModal }: { openModal: () => void }) => {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-b-[13px] bg-neutral-50 p-4">
      <div className="flex flex-col items-center gap-4">
        <div className="flex h-[60px] w-[60px] items-center justify-center rounded-md bg-basic-white">
          <ListPlus size={24} className="text-neutral-400" />
        </div>
        <div className="space-y-1 text-center">
          <p className="font-medium text-base-text">Brak opcji dodatkowych</p>
          <p className="text-sm text-neutral-600">
            Dodaj pierwszą opcję dodatkową
          </p>
        </div>
      </div>
      <ActionButton tone="outline" size="sm" onClick={openModal}>
        <Plus />
        Dodaj opcję dodatkową
      </ActionButton>
    </div>
  );
};

export default TourOptionsConfigCard;
